import { useState } from "react";
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, fetchSignInMethodsForEmail, setPersistence, browserLocalPersistence } from "firebase/auth";
import { auth, googleProvider, db } from "../lib/firebase";
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { Mail, Chrome, Eye, EyeOff, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeColor?: string;
  mode?: 'login' | 'register';
  tier?: 'free' | 'pro' | 'pro_plus';
  requiredEmail?: string;
  pendingPaymentId?: string | null;
  signupSource?: 'course' | 'salepage' | 'coursedee';
  plan?: 'free' | 'basic' | 'premium';
  onLoginSuccess?: () => void;
  redirectPath?: string;
}

export default function AuthModal({ isOpen, onClose, themeColor = "#8B5CF6", mode = 'login', tier, requiredEmail, pendingPaymentId, signupSource = 'course', plan, onLoginSuccess, redirectPath }: AuthModalProps) {
  const navigate = useNavigate();
  
  console.log('[AuthModal] Received props:', { signupSource, plan, mode });
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(mode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleEmailPasswordAuth = async () => {
    if (!email || !email.includes("@")) {
      setError("กรุณากรอก Email ให้ถูกต้อง");
      return;
    }

    if (!password || password.length < 6) {
      setError("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }

    if (activeTab === 'register' && password !== confirmPassword) {
      setError("รหัสผ่านไม่ตรงกัน");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setShowSubscriptionWarning(false);

    // Coursedee = สมัครฟรี/เข้าระบบด้วย email ธรรมดา ไม่ต้องยืนยันอีเมล (ระบบส่งเมลถูกปิด)
    const authHostname = window.location.hostname;
    const isCoursedee = signupSource === 'coursedee'

      || authHostname.includes('aiforyou')
;

    try {
      let result;

      if (activeTab === 'register') {
        console.log('[AuthModal] Registering with email/password');
        result = await createUserWithEmailAndPassword(auth, email, password);
        console.log('[AuthModal] Registration successful:', result.user.uid);

        // Coursedee: ไม่ต้องยืนยันอีเมล (ระบบส่งเมลถูกปิด) — อื่น ๆ ยังส่ง verify ตามเดิม
        if (!isCoursedee) {
          try {
            await sendEmailVerification(result.user);
            console.log('[AuthModal] Verification email sent to:', result.user.email);
          } catch (verifyError) {
            console.error('[AuthModal] Failed to send verification email:', verifyError);
          }
        }

        // Create user document in Firestore
        const userRef = doc(db, "users", result.user.uid);
        await setDoc(userRef, {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.email?.split("@")[0] || "",
          displayName: result.user.displayName || "",
          photoURL: result.user.photoURL || "",
          provider: "email",
          role: "instructor",
          purchasedCourses: [],
          createdAt: new Date(),
          emailVerified: isCoursedee ? true : false,
          signupSource: isCoursedee ? 'coursedee' : signupSource,
          firstLoginAfterVerification: !isCoursedee,
          pendingRedirectPath: redirectPath || null,
        });
        console.log('[AuthModal] User document created with signupSource:', isCoursedee ? 'coursedee' : signupSource, 'pendingRedirectPath:', redirectPath);

        if (!isCoursedee) {
          // Non-coursedee: sign out + require email verification
          await auth.signOut();
          setError("✅ สมัครสมาชิกสำเร็จ!\n\nกรุณาตรวจสอบอีเมลของคุณและคลิกลิงก์ยืนยันเพื่อเปิดใช้งานบัญชี");
          setLoading(false);
          return;
        }
        // Coursedee: อยู่ในระบบต่อเลย แล้วไหลไป redirect ด้านล่าง (ไป dashboard)
        console.log('[AuthModal] Coursedee free signup - staying logged in');
      } else {
        console.log('[AuthModal] Logging in with email/password');
        // Ensure persistence is set to LOCAL before login (important for mobile)
        await setPersistence(auth, browserLocalPersistence);
        result = await signInWithEmailAndPassword(auth, email, password);
        console.log('[AuthModal] Login successful:', result.user.uid);
        
        // Check if email is verified (coursedee ข้าม — สมัคร/เข้าด้วย email ธรรมดา)
        if (!isCoursedee && !result.user.emailVerified) {
          console.log('[AuthModal] Email not verified - sending new verification email');
          
          // Send new verification email
          try {
            const userRef = doc(db, "users", result.user.uid);
            const userDoc = await getDoc(userRef);
            const signupSource = userDoc.exists() ? userDoc.data()?.signupSource : 'edumi';
            
            const actionCodeSettings = {
              url: signupSource === 'coursedee' 
                ? 'https://aiforyou.web.app/email-verify'
                : signupSource === 'salepage'
                ? 'https://saledi.web.app/email-verify'
                : 'https://aiforyou.web.app/email-verify',
              handleCodeInApp: true,
            };
            
            await sendEmailVerification(result.user, actionCodeSettings);
            console.log('[AuthModal] ✅ Verification email resent');
            setError("❌ กรุณายืนยันอีเมลของคุณก่อนเข้าสู่ระบบ\n\n✅ เราได้ส่งอีเมลยืนยันใหม่ให้คุณแล้ว\nกรุณาตรวจสอบอีเมลและคลิกลิงก์ยืนยัน");
          } catch (verifyErr) {
            console.error('[AuthModal] Failed to resend verification email:', verifyErr);
            setError("❌ กรุณายืนยันอีเมลของคุณก่อนเข้าสู่ระบบ\n\nตรวจสอบอีเมลและคลิกลิงก์ยืนยัน");
          }
          
          await auth.signOut();
          setLoading(false);
          return;
        }
        
        // Check if this is first login after verification
        const userRef = doc(db, "users", result.user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.firstLoginAfterVerification === true) {
            // Update flag and clear pending redirect
            await updateDoc(userRef, {
              firstLoginAfterVerification: false,
              pendingRedirectPath: null,
            });
            
            // Use pendingRedirectPath from user document or redirectPath prop
            const savedRedirectPath = userData.pendingRedirectPath || redirectPath;
            
            // If we have a saved redirect path, use it
            if (savedRedirectPath) {
              console.log('[AuthModal] First login after verification - redirecting to saved path:', savedRedirectPath);
              onClose();
              window.location.href = savedRedirectPath;
              return;
            }
            
            // Otherwise, redirect to checkout page based on current domain and plan
            let redirectUrl;
            const currentHostname = window.location.hostname;
            const isSalePageDomain = currentHostname.includes('saledi.web.app');
            console.log('[AuthModal] First login - current domain:', currentHostname, 'isSalePageDomain:', isSalePageDomain, 'plan:', plan);
            if (isSalePageDomain) {
              if (plan === 'free') {
                redirectUrl = 'https://saledi.web.app/salepage/checkout-payment?plan=free';
              } else {
                redirectUrl = `https://saledi.web.app/salepage/checkout?plan=${plan}`;
              }
            } else {
              redirectUrl = 'https://aiforyou.web.app/pricing#packages';
            }
            
            console.log('[AuthModal] First login after verification - redirecting to:', redirectUrl);
            onClose();
            window.location.href = redirectUrl;
            return;
          }
        }
      }
      
      // Skip subscription check for admin, salepage users, coursedee users, or if has onLoginSuccess callback
      const isAdmin = result.user.email === 'viitamint@gmail.com';
      const currentHostname = window.location.hostname;
      const isSalePageLogin = currentHostname.includes('saledi.web.app');
      const isCoursedeeLogin = currentHostname.includes('aiforyou');
      
      if (onLoginSuccess) {
        console.log('[AuthModal] Has onLoginSuccess callback - skipping subscription check');
      } else if (isSalePageLogin) {
        console.log('[AuthModal] SalePage login - skipping course subscription check');
      } else if (isCoursedeeLogin) {
        console.log('[AuthModal] Coursedee login - skipping course subscription check');
      } else if (!isAdmin) {
        // Check if user has subscription (only for course platform)
        const subscriptionsRef = collection(db, "subscriptions");
        const subscriptionQuery = query(
          subscriptionsRef,
          where("userId", "==", result.user.uid),
          where("status", "in", ["active", "grace_period"])
        );
        const subscriptionSnapshot = await getDocs(subscriptionQuery);
        
        // If no active subscription, check if they have Coursedee subscription
        if (subscriptionSnapshot.empty) {
          console.log('[AuthModal] No course subscription found, checking for Coursedee subscription...');
          
          // Check subscription_payments for Coursedee by email (since userId might not be set yet)
          const paymentsRef = collection(db, "subscription_payments");
          const paymentsQuery = query(
            paymentsRef,
            where("email", "==", result.user.email),
            where("status", "in", ["approved", "verified"])
          );
          const paymentsSnapshot = await getDocs(paymentsQuery);
          
          console.log('[AuthModal] Found', paymentsSnapshot.docs.length, 'payment records for email:', result.user.email);
          
          const hasCoursedee = paymentsSnapshot.docs.some(doc => {
            const data = doc.data();
            const isCoursedee = data.planName?.includes('Coursedee') || data.source === 'coursedee';
            console.log('[AuthModal] Payment record:', { planName: data.planName, source: data.source, isCoursedee });
            return isCoursedee;
          });
          
          if (!hasCoursedee) {
            console.log('[AuthModal] No Coursedee subscription found for email/password user');
            await auth.signOut();
            setShowSubscriptionWarning(true);
            setLoading(false);
            return;
          } else {
            console.log('[AuthModal] ✅ Found Coursedee subscription - allowing login');
          }
        }
      } else {
        console.log('[AuthModal] Admin login - skipping subscription check');
      }

      // Update user document if exists
      const userRef2 = doc(db, "users", result.user.uid);
      const userDoc2 = await getDoc(userRef2);
      
      if (!userDoc2.exists()) {
        console.log('[AuthModal] Creating new user document in Firestore');
        await setDoc(userRef2, {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.email?.split("@")[0] || "",
          displayName: result.user.displayName || "",
          photoURL: result.user.photoURL || "",
          provider: "email",
          role: "instructor",
          purchasedCourses: [],
          createdAt: new Date(),
        });
        console.log('[AuthModal] User document created with role: instructor');
      }
      
      // If onLoginSuccess callback exists, use it instead of reloading
      if (onLoginSuccess) {
        console.log('[AuthModal] Email/Password login successful - calling onLoginSuccess callback');
        onClose();
        onLoginSuccess();
        return;
      }
      
      // Handle redirect path if provided
      if (redirectPath) {
        console.log('[AuthModal] Email/Password login successful - redirecting to:', redirectPath);
        onClose();
        window.location.href = redirectPath;
        return;
      }
      
      // Coursedee: หลังสมัคร/เข้าระบบ ไปหน้า dashboard เสมอ (ซื้อคอร์สค่อยปลดล็อก AI)
      if (isCoursedee) {
        console.log('[AuthModal] Coursedee auth success - redirecting to dashboard');
        onClose();
        window.location.href = '/coursedee/dashboard';
        return;
      }

      // Check current domain to determine where to redirect
      const emailLoginHostname = window.location.hostname;
      const isEmailOnSalePageDomain = emailLoginHostname.includes('saledi.web.app');
      
      // If on salepage domain and signing up for salepage with a plan, go to checkout
      if (isEmailOnSalePageDomain && signupSource === 'salepage' && plan) {
        console.log('[AuthModal] On SalePage domain with salepage signup and plan - redirecting to checkout');
        onClose();
        window.location.href = `https://saledi.web.app/salepage/checkout-payment?plan=${plan}`;
        return;
      }
      
      // If on salepage domain, redirect to salepage dashboard
      if (isEmailOnSalePageDomain) {
        console.log('[AuthModal] On SalePage domain - redirecting to salepage dashboard');
        onClose();
        window.location.href = 'https://saledi.web.app/salepage/dashboard';
        return;
      }
      
      // For course platform, reload the page
      console.log('[AuthModal] On Course platform - reloading page');
      onClose();
      window.location.reload();
    } catch (err: any) {
      console.error('[AuthModal] Email/Password auth error:', err);
      
      let errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      
      if (err.code === 'auth/invalid-email') {
        errorMessage = "รูปแบบอีเมลไม่ถูกต้อง";
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        // Check if this email is registered with Gmail OAuth
        try {
          const signInMethods = await fetchSignInMethodsForEmail(auth, email);
          if (signInMethods.includes('google.com')) {
            errorMessage = "⚠️ บัญชีนี้สมัครด้วย Gmail\n\nกรุณากดปุ่ม 'Login ด้วย Gmail' ด้านล่าง\n\nหรือไปที่หน้าโปรไฟล์เพื่อตั้งรหัสผ่าน";
          } else {
            errorMessage = "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
          }
        } catch (fetchErr) {
          console.error('[AuthModal] Error fetching sign-in methods:', fetchErr);
          errorMessage = "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
        }
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = "มีการพยายามเข้าสู่ระบบมากเกินไป กรุณารอสักครู่แล้วลองใหม่";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !resetEmail.includes("@")) {
      setError("กรุณากรอกอีเมลให้ถูกต้อง");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setSuccess("✅ ส่งลิงก์รีเซ็ตรหัสผ่านไปยังอีเมลของคุณแล้ว\n\nกรุณาตรวจสอบอีเมลและคลิกลิงก์เพื่อรีเซ็ตรหัสผ่าน");
      setResetEmail("");
    } catch (err: any) {
      console.error('[AuthModal] Password reset error:', err);
      
      let errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = "ไม่พบอีเมลนี้ในระบบ";
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = "รูปแบบอีเมลไม่ถูกต้อง";
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = "มีการร้องขอมากเกินไป กรุณารอสักครู่แล้วลองใหม่";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    console.log('[AuthModal] Google Login initiated');
    setLoading(true);
    setError("");
    setShowSubscriptionWarning(false);

    try {
      console.log('[AuthModal] Starting Google signInWithPopup...');
      // Ensure persistence is set to LOCAL before login (important for mobile)
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if email matches required email (for paid package flow)
      if (requiredEmail && result.user.email?.toLowerCase() !== requiredEmail.toLowerCase()) {
        await auth.signOut();
        setError(`กรุณาใช้อีเมล ${requiredEmail} ที่คุณใช้ชำระเงิน\n\nอีเมลที่คุณเลือก: ${result.user.email}`);
        setLoading(false);
        return;
      }
      
      console.log('[AuthModal] Google login successful:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });

      // Skip subscription check for admin or salepage users
      const isAdmin = result.user.email === 'viitamint@gmail.com';
      const googleHostname = window.location.hostname;
      const isSalePageLogin = googleHostname.includes('saledi.web.app');
      
      // Create or update user in Firestore
      const userRef = doc(db, "users", result.user.uid);
      const userDoc = await getDoc(userRef);

      const isNewUser = !userDoc.exists();

      // Coursedee: Google login/signup → เข้า dashboard เลย (ไม่ต้องเช็ค subscription/verify)
      const isCoursedee = signupSource === 'coursedee'

        || googleHostname.includes('aiforyou')
;
      if (isCoursedee) {
        if (isNewUser) {
          await setDoc(userRef, {
            uid: result.user.uid,
            email: result.user.email,
            name: result.user.displayName || "",
            displayName: result.user.displayName || "",
            photoURL: result.user.photoURL || "",
            provider: "google",
            role: "instructor",
            purchasedCourses: [],
            createdAt: new Date(),
            emailVerified: true,
            signupSource: 'coursedee',
          });
          console.log('[AuthModal] Coursedee Google signup - new user doc created');
        }
        onClose();
        window.location.href = redirectPath || '/coursedee/dashboard';
        return;
      }

      if (isNewUser) {
        console.log('[AuthModal] Creating new user document in Firestore');
        await setDoc(userRef, {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || "",
          displayName: result.user.displayName || "",
          photoURL: result.user.photoURL || "",
          provider: "google",
          role: "instructor",
          purchasedCourses: [],
          createdAt: new Date(),
          storageUsedBytes: 0,
          addons: {
            extraStorageGB: 0,
            active: false
          },
          signupSource: signupSource,
          firstLoginAfterVerification: true,
          pendingRedirectPath: redirectPath || null,
        });
        console.log('[AuthModal] User document created with role: instructor and signupSource:', signupSource, 'pendingRedirectPath:', redirectPath);
      } else {
        console.log('[AuthModal] User already exists in Firestore');
        
        // Check if this is first login (for existing users who just signed up)
        const userData = userDoc.data();
        if (userData.firstLoginAfterVerification === true) {
          // Update flag and clear pending redirect
          await updateDoc(userRef, {
            firstLoginAfterVerification: false,
            pendingRedirectPath: null,
          });
          
          // If onLoginSuccess callback exists, use it instead of redirecting
          if (onLoginSuccess) {
            console.log('[AuthModal] First login after Google sign-up - calling onLoginSuccess callback');
            onClose();
            onLoginSuccess();
            return;
          }
          
          // Use pendingRedirectPath from user document or redirectPath prop
          const savedRedirectPath = userData.pendingRedirectPath || redirectPath;
          
          // If we have a saved redirect path, use it
          if (savedRedirectPath) {
            console.log('[AuthModal] First login after Google sign-up - redirecting to saved path:', savedRedirectPath);
            onClose();
            window.location.href = savedRedirectPath;
            return;
          }
          
          // Otherwise, redirect to checkout page based on current domain and plan
          let redirectUrl;
          const googleCurrentHostname = window.location.hostname;
          const googleIsSalePageDomain = googleCurrentHostname.includes('saledi.web.app');
          console.log('[AuthModal] Google login - current domain:', googleCurrentHostname, 'isSalePageDomain:', googleIsSalePageDomain, 'plan:', plan);
          if (googleIsSalePageDomain) {
            if (plan === 'free') {
              redirectUrl = 'https://saledi.web.app/salepage/checkout-payment?plan=free';
            } else {
              redirectUrl = `https://saledi.web.app/salepage/checkout?plan=${plan}`;
            }
          } else {
            redirectUrl = 'https://aiforyou.web.app/pricing#packages';
          }
          
          console.log('[AuthModal] First login after Google sign-up - redirecting to:', redirectUrl);
          onClose();
          window.location.href = redirectUrl;
          return;
        }
      }

      if (!isAdmin && !isSalePageLogin) {
        // Check if user has subscription (only for course platform)
        const subscriptionsRef = collection(db, "subscriptions");
        const subscriptionQuery = query(
          subscriptionsRef,
          where("userId", "==", result.user.uid)
        );
        const subscriptionSnapshot = await getDocs(subscriptionQuery);
        
        // REGISTER MODE: Create Free Trial for new users (only if never used before)
        if (mode === 'register' && tier === 'free' && subscriptionSnapshot.empty) {
          // Check if user has already used free trial
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();
          
          if (userData?.hasUsedFreeTrial) {
            console.log('[AuthModal] User has already used free trial - cannot create new trial');
            alert('คุณเคยใช้ Free Trial ไปแล้ว กรุณาสมัครแพ็คเก็จเพื่อใช้งานต่อ');
            await auth.signOut();
            setLoading(false);
            return;
          }
          
          console.log('[AuthModal] Register mode - creating Free Trial subscription');
          
          const now = new Date();
          const endDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days
          const gracePeriodEndsAt = new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000); // +7 days grace period
          
          await setDoc(doc(db, "subscriptions", result.user.uid), {
            userId: result.user.uid,
            tier: "free",
            status: "active",
            startDate: now,
            endDate: endDate,
            gracePeriodDays: 7,
            gracePeriodEndsAt: gracePeriodEndsAt,
            autoRenew: false,
            createdAt: now,
            updatedAt: now
          });
          
          // Update user's subscription tier and mark free trial as used
          await updateDoc(userRef, {
            subscriptionTier: "free",
            subscriptionEndDate: endDate,
            hasUsedFreeTrial: true,
            firstLoginAfterVerification: false,
          });
          
          console.log('[AuthModal] Free Trial subscription created - redirecting to verify-email');
          onClose();
          navigate('/verify-email');
          return;
        }
        
        // For new Google users without tier specified, redirect to pricing
        if (isNewUser && subscriptionSnapshot.empty) {
          console.log('[AuthModal] New Google user - redirecting to pricing page');
          
          // Get saved redirect path from user document
          const userDocData = await getDoc(userRef);
          const savedRedirectPath = userDocData.exists() ? userDocData.data()?.pendingRedirectPath : null;
          
          // Update flag and clear pending redirect
          await updateDoc(userRef, {
            firstLoginAfterVerification: false,
            pendingRedirectPath: null,
          });
          
          // If onLoginSuccess callback exists, use it instead of redirecting
          if (onLoginSuccess) {
            console.log('[AuthModal] New Google user - calling onLoginSuccess callback');
            onClose();
            onLoginSuccess();
            return;
          }
          
          // Use saved redirect path or redirectPath prop
          const finalRedirectPath = savedRedirectPath || redirectPath;
          
          // If we have a redirect path, use it
          if (finalRedirectPath) {
            console.log('[AuthModal] New Google user - redirecting to saved path:', finalRedirectPath);
            onClose();
            window.location.href = finalRedirectPath;
            return;
          }
          
          // Otherwise, redirect to checkout page based on current domain and plan
          let redirectUrl;
          const newUserHostname = window.location.hostname;
          const newUserIsSalePageDomain = newUserHostname.includes('saledi.web.app');
          console.log('[AuthModal] New Google user - current domain:', newUserHostname, 'isSalePageDomain:', newUserIsSalePageDomain, 'plan:', plan);
          if (newUserIsSalePageDomain) {
            if (plan === 'free') {
              redirectUrl = 'https://saledi.web.app/salepage/checkout-payment?plan=free';
            } else {
              redirectUrl = `https://saledi.web.app/salepage/checkout?plan=${plan}`;
            }
          } else {
            redirectUrl = 'https://aiforyou.web.app/pricing#packages';
          }
          
          console.log('[AuthModal] Redirecting new Google user to:', redirectUrl);
          onClose();
          window.location.href = redirectUrl;
          return;
        }
        
        // If has onLoginSuccess callback, skip subscription check entirely
        if (!onLoginSuccess) {
          if (subscriptionSnapshot.empty && activeTab === 'login') {
            // LOGIN MODE: Check if subscription exists
            console.log('[AuthModal] Login mode - no subscription found, signing out');
            await auth.signOut();
            setShowSubscriptionWarning(true);
            setLoading(false);
            return;
          } else if (subscriptionSnapshot.empty) {
            // Check if user has already used free trial
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();
            
            if (userData?.hasUsedFreeTrial) {
              console.log('[AuthModal] User has already used free trial - cannot create new trial');
              alert('คุณเคยใช้ Free Trial ไปแล้ว กรุณาสมัครแพ็คเก็จเพื่อใช้งานต่อ');
              await auth.signOut();
              setLoading(false);
              return;
            }
            
            // If user logs in without subscription (register mode), create free trial
            console.log('[AuthModal] No subscription found - creating Free Trial');
            
            const now = new Date();
            const endDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            const gracePeriodEndsAt = new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            
            await setDoc(doc(db, "subscriptions", result.user.uid), {
              userId: result.user.uid,
              tier: "free",
              status: "active",
              startDate: now,
              endDate: endDate,
              gracePeriodDays: 7,
              gracePeriodEndsAt: gracePeriodEndsAt,
              autoRenew: false,
              createdAt: now,
              updatedAt: now
            });
            
            await setDoc(userRef, {
              subscriptionTier: "free",
              subscriptionEndDate: endDate,
              hasUsedFreeTrial: true
            }, { merge: true });
          }
        }
      } else if (isAdmin) {
        console.log('[AuthModal] Admin login - skipping subscription check');
      } else if (isSalePageLogin) {
        console.log('[AuthModal] SalePage login - skipping course subscription check');
      }
      
      // Link pending payment if exists
      if (pendingPaymentId && result.user.uid) {
        try {
          console.log('[AuthModal] Linking payment to user:', pendingPaymentId);
          const paymentRef = doc(db, 'subscriptionPayments', pendingPaymentId);
          await updateDoc(paymentRef, {
            userId: result.user.uid,
            linkedToUser: true,
            linkedAt: new Date()
          });
          console.log('[AuthModal] Payment linked successfully');
        } catch (linkError) {
          console.error('[AuthModal] Error linking payment:', linkError);
        }
      }
      
      // If onLoginSuccess callback exists, use it instead of navigating
      if (onLoginSuccess) {
        console.log('[AuthModal] Google login successful - calling onLoginSuccess callback');
        onClose();
        onLoginSuccess();
        return;
      }
      
      // Handle redirect path if provided
      if (redirectPath) {
        console.log('[AuthModal] Google login successful - redirecting to:', redirectPath);
        onClose();
        window.location.href = redirectPath;
        return;
      }
      
      // Check current domain to determine where to redirect
      const currentHostname = window.location.hostname;
      const isSalePageDomain = currentHostname.includes('saledi.web.app');
      
      // If on salepage domain, redirect to salepage dashboard
      if (isSalePageDomain) {
        console.log('[AuthModal] On SalePage domain - redirecting to salepage dashboard');
        onClose();
        window.location.href = 'https://saledi.web.app/salepage/dashboard';
        return;
      }
      
      // For course platform (edumi.co), redirect to verify-email or reload
      console.log('[AuthModal] On Course platform - staying on course platform');
      if (!result.user.emailVerified) {
        onClose();
        navigate('/verify-email');
      } else {
        onClose();
        window.location.reload();
      }
    } catch (err: any) {
      console.error('[AuthModal] Google login error:', err);
      
      let errorMessage = "ไม่สามารถเข้าสู่ระบบด้วย Google ได้ กรุณาลองใหม่อีกครั้ง";
      
      if (err.code === 'auth/operation-not-allowed') {
        errorMessage = "⚠️ Google Sign-in ยังไม่ได้เปิดใช้งาน\n\nกรุณาติดต่อผู้ดูแลระบบเพื่อเปิดใช้งาน Google Provider ใน Firebase Console";
        console.error('[AuthModal] ❌ Google Provider is NOT ENABLED in Firebase Console!');
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = "คุณปิดหน้าต่าง Google Login กรุณาลองใหม่อีกครั้ง";
      } else if (err.code === 'auth/unauthorized-domain') {
        errorMessage = "โดเมนนี้ไม่ได้รับอนุญาตให้ใช้ Google Login";
        console.error('[AuthModal] ❌ Domain not authorized:', window.location.hostname);
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = "เบราว์เซอร์บล็อก Popup กรุณาอนุญาต Popup สำหรับเว็บไซต์นี้";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
        <div className="bg-white rounded-3xl shadow-2xl max-w-[440px] w-full p-6 relative animate-fadeIn my-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
          >
            ×
          </button>

          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">ลืมรหัสผ่าน?</h2>
            <p className="text-gray-600 text-sm">
              กรุณากรอกอีเมลและกดปุ่มส่ง<br />
              ลิงก์รีเซ็ตรหัสผ่านจะถูกส่งไปยังอีเมลของคุณ
            </p>
          </div>

          <div className="space-y-4">
            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3">
                <p className="text-green-700 text-xs whitespace-pre-line">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                <p className="text-red-700 text-xs whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                อีเมล
              </label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleForgotPassword();
                  }
                }}
              />
            </div>

            {/* Send Button */}
            <button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  กำลังส่ง...
                </div>
              ) : (
                'ส่งลิงก์รีเซ็ตรหัสผ่าน'
              )}
            </button>

            {/* Back Button */}
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmail("");
                setError("");
                setSuccess("");
              }}
              disabled={loading}
              className="w-full px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors disabled:opacity-50"
            >
              ← กลับไปหน้าเข้าสู่ระบบ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-[440px] w-full p-6 relative animate-fadeIn my-4 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
        >
          ×
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {showEmailForm ? (activeTab === 'register' ? 'สมัครสมาชิกฟรี' : 'เข้าสู่ระบบ') : 'เข้าสู่ระบบ / สมัครสมาชิก'}
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            {showEmailForm ? 'ใช้อีเมลและรหัสผ่านของคุณ' : 'คลิกเดียวด้วย Google เริ่มใช้งานได้ทันที'}
          </p>
        </div>

        {/* Messages */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3 mb-4">
            <p className="text-green-700 text-xs whitespace-pre-line">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-xs whitespace-pre-line">{error}</p>
          </div>
        )}

        {/* Google Login - primary method */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Chrome className="w-5 h-5 text-purple-600" />
          )}
          เข้าสู่ระบบด้วย Google
        </button>

        {/* Toggle: email form */}
        {!showEmailForm ? (
          <button
            type="button"
            onClick={() => { setShowEmailForm(true); setError(''); setSuccess(''); }}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-4 transition-colors"
            disabled={loading}
          >
            หรือ เข้าสู่ระบบด้วยอีเมล
          </button>
        ) : (
          <>
            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">หรือใช้อีเมล</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-5 bg-gray-100 rounded-xl p-1">
              <button
                type="button"
                onClick={() => { setActiveTab('login'); setError(''); setSuccess(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'login' ? 'bg-white text-purple-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                disabled={loading}
              >
                เข้าสู่ระบบ
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab('register'); setError(''); setSuccess(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'register' ? 'bg-white text-purple-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                disabled={loading}
              >
                สมัครสมาชิก
              </button>
            </div>
          </>
        )}

        <div className="space-y-4">
          {/* LOGIN TAB */}
          {showEmailForm && activeTab === 'login' && (
            <>
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  อีเมล
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                  disabled={loading}
                />
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสผ่าน
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors pr-12"
                    disabled={loading}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEmailPasswordAuth();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">จดจำการล็อกอิน</span>
                </label>
              </div>

              {/* Login Button */}
              <button
                onClick={handleEmailPasswordAuth}
                disabled={loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังเข้าสู่ระบบ...
                  </div>
                ) : (
                  'ล็อกอิน'
                )}
              </button>

              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-purple-600 hover:text-purple-700 hover:underline transition-colors"
                  disabled={loading}
                >
                  ลืมรหัสผ่านใช่ไหม?
                </button>
              </div>

            </>
          )}

          {/* REGISTER TAB */}
          {showEmailForm && activeTab === 'register' && (
            <>
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  อีเมล
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                  disabled={loading}
                />
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสผ่าน
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="อย่างน้อย 6 ตัวอักษร"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors pr-12"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ยืนยันรหัสผ่าน
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="กรอกรหัสผ่านอีกครั้ง"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none transition-colors pr-12"
                    disabled={loading}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEmailPasswordAuth();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Register Button */}
              <button
                onClick={handleEmailPasswordAuth}
                disabled={loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังสมัคร...
                  </div>
                ) : (
                  'สมัครสมาชิกฟรี'
                )}
              </button>
            </>
          )}

        </div>

        {/* Terms and Conditions */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            การเข้าสู่ระบบหมายถึงคุณยอมรับ<br />
            <a href="/terms" className="underline hover:text-gray-700">เงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว</a>
          </p>
        </div>
      </div>
    </div>
  );
}
